import type { Analytics } from "./analytics.js";

/**
 * Zero-config SPA navigation tracking. Monkey-patches `history.pushState`
 * and `history.replaceState` (which don't emit events natively) and listens
 * to `popstate`. Every URL change triggers `analytics.pageView()`.
 *
 * Works with React Router, Vue Router, SvelteKit, plain history-API code —
 * anything that uses the standard History API. RouterTracker remains
 * available for users who prefer an explicit hook; pageView() is idempotent
 * on the same path so the two do not double-emit.
 */
export function attachHistoryTracking(a: Analytics): () => void {
  if (typeof window === "undefined" || typeof history === "undefined") return () => {};

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  const fire = (): void => {
    // A microtask gives the framework a chance to settle window.location
    // before we read it, which avoids stale-URL pageViews on some routers.
    queueMicrotask(() => a.pageView());
  };

  history.pushState = function (data: unknown, unused: string, url?: string | URL | null) {
    const ret = originalPushState(data, unused, url);
    fire();
    return ret;
  };
  history.replaceState = function (data: unknown, unused: string, url?: string | URL | null) {
    const ret = originalReplaceState(data, unused, url);
    fire();
    return ret;
  };

  const onPopState = (): void => fire();
  window.addEventListener("popstate", onPopState);

  return () => {
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    window.removeEventListener("popstate", onPopState);
  };
}
